/**
 * Splats in front of water: the World splat draw split at the water surface, so water sorts with
 * the splats as a mirror does (a mirror is opaque: it draws before the splats and writes depth).
 *
 * High / low tier water draws after the splats (WaterLayer, see waterSystem.ts), so whatever the
 * splat draw left in the frame under a water surface is painted over by it. Without the split the
 * water hides itself only where the splat depth map (SplatDepthPass: a splat's centre depth
 * wherever its alpha is above 0.3) holds something in front of it. Soft, low-alpha splats rarely
 * enter that map, so over water their soft parts vanished under it and only their cores showed,
 * as hard discs: clouds, splat weather, and the scan's own soft parts above the water (a tree
 * canopy over a pond).
 *
 * While a water surface is on screen, the main camera's
 * unified splat draw is split in two at the water, per fragment, with the gsplatModifyPS hook
 * (the one per-fragment hook both splat renderers share, installed on the scene's template
 * material, so WebGL2's CPU-sort and WebGPU's GPU-sort draws both take it):
 * - pass A, the ordinary World draw: fragments of splats in front of the water are discarded;
 * - the water draws over pass A (refraction copy, depth colour, shore, foam as before);
 * - pass B, the same draw again right after the water (WaterLayer's postrender:layer, the way
 *   SplatDepthPass reuses the camera's own draw, sort and work buffer), scissored to the water's
 *   screen rectangle: only the fragments pass A dropped are kept, and they blend over the water
 *   with their real alpha.
 * A fragment counts as in front when the point on its pixel's view ray at its splat's centre depth
 * is more than `splitHeight` above the surface, on the camera's side: the same per-pixel test the
 * water's own coverage makes against the depth map (its fade starts at that height), so the scan's
 * noisy pond-surface splats stay under the water. Both passes blend in the camera's sort order, so
 * per pixel the result is the sorted composite with the water inserted where it belongs; away from
 * the water the two passes add up to exactly the unsplit draw.
 *
 * The water also needs to see past those splats: its depth map must hold only what pass A draws
 * (otherwise a cloud core in front reads as the pond's bottom and the water vanishes there). While
 * the split is on, SplatDepthPass renders the water's map with the same hook in a depth mode (see
 * `bindDepthMode`); translucent entities and particles keep the full map.
 *
 * One split plane per frame: all water planes on screen must be parallel with the camera on the
 * same side of every one, and the split is at the one nearest the camera (the first surface every
 * view ray crosses), so nothing behind any water is ever drawn over it. Otherwise (the camera
 * between two water levels, tilted planes at different angles) the split is off for that frame and
 * the water draws as before.
 *
 * Cost: nothing for scenes without water (the chunk isn't installed). With water but none on
 * screen, a uniform test per splat fragment. While on: the water's
 * depth map is the same one pass (a second one only while translucent entities or particles also
 * need the full map); pass B re-runs the splat vertex stage and rasterises the splats inside the
 * water's screen rectangle once more. Measured on the golf scan (4.45M splats, RTX 4080, pond on
 * screen): +11% GPU time on WebGPU, where pass B reads the GPU sort's projection cache. On the
 * CPU-sort renderer (WebGL2) every splat pass re-runs the full vertex stage (+45%), so there the
 * split runs only within CPU_SORT_SPLIT_SPLAT_LIMIT and for water large on screen, and the water's
 * depth map culls in the vertex stage with the prepass shader (`vertexCullPlane`).
 */
import * as pc from 'playcanvas';
import { collectUnifiedSplatDraws } from '../../dynamic-viewer/splatMeshInstances';

/** Uniforms of the gsplatModifyPS hook (device scope, set around each draw that uses them). */
export const WATER_SPLIT_UNIFORMS = {
    /** x: mode (WATER_SPLIT_MODE), y: alpha clip for the depth mode. */
    mode: 'uWaterSplit',
    /** The split surface over (fragment x, fragment y, window depth, 1): positive in front of the water. */
    plane: 'uWaterSplitPlane',
    /** Fragment pixels (x0, y0, x1, y1) the split applies to (the water's screen rectangle). */
    rect: 'uWaterSplitRect',
    /** Window depth z to linear depth: (x + y z) / (z' + w z). */
    depth: 'uWaterSplitDepth',
} as const;

export const WATER_SPLIT_MODE = {
    off: 0,
    /** Pass A: drop the fragments in front of the water. */
    behind: 1,
    /** Pass B: keep only the fragments in front of the water. */
    front: 2,
    /** The water's depth map: linear depth of the fragments behind the water above the alpha clip. */
    depth: 3,
} as const;

const MARKER = 'STORYSPLAT_WATER_SPLIT';

export const WATER_SPLIT_PS_GLSL = /* glsl */ `
// ${MARKER} (effects/water/waterSplatSplit.ts)
uniform vec4 ${WATER_SPLIT_UNIFORMS.mode};
uniform vec4 ${WATER_SPLIT_UNIFORMS.plane};
uniform vec4 ${WATER_SPLIT_UNIFORMS.rect};
uniform vec4 ${WATER_SPLIT_UNIFORMS.depth};
void modifySplatColor(vec2 gaussianUV, inout vec4 color) {
    float mode = ${WATER_SPLIT_UNIFORMS.mode}.x;
    if (mode < 0.5) return;
    vec4 p = vec4(gl_FragCoord.xy, gl_FragCoord.z, 1.0);
    vec4 r = ${WATER_SPLIT_UNIFORMS.rect};
    bool front = p.x >= r.x && p.y >= r.y && p.x < r.z && p.y < r.w && dot(${WATER_SPLIT_UNIFORMS.plane}, p) > 0.0;
    if (mode < 1.5) {
        if (front) discard;
        return;
    }
    if (mode < 2.5) {
        if (!front) discard;
        return;
    }
    if (front || color.a < ${WATER_SPLIT_UNIFORMS.mode}.y) discard;
    vec4 k = ${WATER_SPLIT_UNIFORMS.depth};
    color = vec4((k.x + k.y * p.z) / (k.z + k.w * p.z), 0.0, 0.0, 1.0);
}
`;

export const WATER_SPLIT_PS_WGSL = /* wgsl */ `
// ${MARKER} (effects/water/waterSplatSplit.ts)
uniform ${WATER_SPLIT_UNIFORMS.mode}: vec4f;
uniform ${WATER_SPLIT_UNIFORMS.plane}: vec4f;
uniform ${WATER_SPLIT_UNIFORMS.rect}: vec4f;
uniform ${WATER_SPLIT_UNIFORMS.depth}: vec4f;
fn modifySplatColor(gaussianUV: vec2f, color: ptr<function, vec4f>) {
    let mode = uniform.${WATER_SPLIT_UNIFORMS.mode}.x;
    if (mode < 0.5) { return; }
    let p = vec4f(pcPosition.xy, pcPosition.z, 1.0);
    let r = uniform.${WATER_SPLIT_UNIFORMS.rect};
    let front = p.x >= r.x && p.y >= r.y && p.x < r.z && p.y < r.w && dot(uniform.${WATER_SPLIT_UNIFORMS.plane}, p) > 0.0;
    if (mode < 1.5) {
        if (front) { discard; }
        return;
    }
    if (mode < 2.5) {
        if (!front) { discard; }
        return;
    }
    if (front || (*color).a < uniform.${WATER_SPLIT_UNIFORMS.mode}.y) { discard; }
    let k = uniform.${WATER_SPLIT_UNIFORMS.depth};
    *color = vec4f((k.x + k.y * p.z) / (k.z + k.w * p.z), 0.0, 0.0, 1.0);
}
`;

// ── math (column-major 4x4, like pc.Mat4.data; float64) ───────────────────────────────────

type Mat4Like = ArrayLike<number>;

export function mul4(a: Mat4Like, b: Mat4Like, out = new Float64Array(16)): Float64Array {
    for (let c = 0; c < 4; c++) {
        for (let r = 0; r < 4; r++) {
            let s = 0;
            for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
            out[c * 4 + r] = s;
        }
    }
    return out;
}

/** General 4x4 inverse (cofactors); null when singular. */
export function invert4(m: Mat4Like, out = new Float64Array(16)): Float64Array | null {
    const [a00, a01, a02, a03, a10, a11, a12, a13, a20, a21, a22, a23, a30, a31, a32, a33] = Array.from(m);
    const b00 = a00 * a11 - a01 * a10;
    const b01 = a00 * a12 - a02 * a10;
    const b02 = a00 * a13 - a03 * a10;
    const b03 = a01 * a12 - a02 * a11;
    const b04 = a01 * a13 - a03 * a11;
    const b05 = a02 * a13 - a03 * a12;
    const b06 = a20 * a31 - a21 * a30;
    const b07 = a20 * a32 - a22 * a30;
    const b08 = a20 * a33 - a23 * a30;
    const b09 = a21 * a32 - a22 * a31;
    const b10 = a21 * a33 - a23 * a31;
    const b11 = a22 * a33 - a23 * a32;
    const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
    if (!Number.isFinite(det) || Math.abs(det) < 1e-300) return null;
    const inv = 1 / det;
    out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * inv;
    out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * inv;
    out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * inv;
    out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * inv;
    out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * inv;
    out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * inv;
    out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * inv;
    out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * inv;
    out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * inv;
    out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * inv;
    out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * inv;
    out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * inv;
    out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * inv;
    out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * inv;
    out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * inv;
    out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * inv;
    return out;
}

/**
 * How a target's fragments relate to the camera's clip space. `yDown`: fragment y grows as clip y
 * falls (WebGPU's framebuffer, or a flipped target on WebGL; not both).
 */
export interface FragmentSpace {
    width: number;
    height: number;
    yDown: boolean;
}

/** WebGPU's framebuffer runs top-down, WebGL's bottom-up; a flipY target flips either. */
export function fragmentYDown(webgpu: boolean, flipY: boolean): boolean {
    return webgpu !== flipY;
}

/**
 * World → fragment (x pixels, y pixels, window depth, 1) as a homogeneous matrix: window depth is
 * clip z mapped from -1..1 to 0..1, which is what both backends rasterise (PlayCanvas remaps
 * WebGPU's projection to 0..1 itself).
 */
export function worldToFragment(viewProj: Mat4Like, space: FragmentSpace, out = new Float64Array(16)): Float64Array {
    const { width: w, height: h } = space;
    const sy = space.yDown ? -1 : 1;
    // S: ndc → fragment, column-major
    const s = [
        w / 2, 0, 0, 0,
        0, (sy * h) / 2, 0, 0,
        0, 0, 0.5, 0,
        w / 2, h / 2, 0.5, 1,
    ];
    return mul4(s, viewProj, out);
}

export interface PlaneLike {
    normal: { x: number; y: number; z: number };
    d: number;
}

/** The chosen split: the plane (unit normal toward the camera), and how far above it splats count as in front. */
export interface WaterSplitChoice {
    nx: number;
    ny: number;
    nz: number;
    /** normal · p = d on the surface. */
    d: number;
    height: number;
}

/**
 * The surface to split at, or null when no single plane is safe: every plane parallel (normals
 * within ~1°, either sign) with the camera on the same side of all of them, split at the nearest.
 */
export function chooseWaterSplitPlane(planes: ReadonlyArray<PlaneLike & { splitHeight: number }>, cam: { x: number; y: number; z: number }): WaterSplitChoice | null {
    if (!planes.length) return null;
    const n0 = planes[0].normal;
    const len0 = Math.hypot(n0.x, n0.y, n0.z);
    if (!(len0 > 1e-9)) return null;
    const rx = n0.x / len0, ry = n0.y / len0, rz = n0.z / len0;
    let best: WaterSplitChoice | null = null;
    let bestDist = Infinity;
    let side = 0;
    for (const plane of planes) {
        const n = plane.normal;
        const len = Math.hypot(n.x, n.y, n.z);
        if (!(len > 1e-9) || !Number.isFinite(plane.d)) return null;
        const dot = (n.x * rx + n.y * ry + n.z * rz) / len;
        if (Math.abs(dot) < Math.cos((1 * Math.PI) / 180)) return null;
        // the plane in the reference orientation: r · p = d
        const d = (plane.d / len) * Math.sign(dot);
        const dist = rx * cam.x + ry * cam.y + rz * cam.z - d;
        const s = Math.sign(dist);
        if (s === 0) return null;
        if (side === 0) side = s;
        else if (s !== side) return null;
        if (Math.abs(dist) < bestDist) {
            bestDist = Math.abs(dist);
            // normal toward the camera
            best = { nx: rx * s, ny: ry * s, nz: rz * s, d: d * s, height: Math.max(0, plane.splitHeight) };
        }
    }
    return best;
}

/**
 * The split surface over fragment coordinates: dot(result, (fx, fy, depth, 1)) > 0 exactly where
 * the world point is more than `choice.height` above the plane, toward the camera. A plane through
 * a projective map stays a plane: its coefficients are the inverse-transposed map applied to it,
 * and the homogeneous w (the clip w) is positive in front of the camera, so the sign carries over.
 */
export function splitPlaneCoefficients(worldToFrag: Mat4Like, choice: WaterSplitChoice, out = new Float32Array(4)): Float32Array | null {
    const inv = invert4(worldToFrag);
    if (!inv) return null;
    const pi = [choice.nx, choice.ny, choice.nz, -(choice.d + choice.height)];
    // (M^-1)^T π: component c is column c of M^-1 dotted with π
    for (let c = 0; c < 4; c++) out[c] = inv[c * 4] * pi[0] + inv[c * 4 + 1] * pi[1] + inv[c * 4 + 2] * pi[2] + inv[c * 4 + 3] * pi[3];
    return out;
}

/**
 * Window depth z → linear view depth for a projection (GL convention, as pc.Camera keeps it):
 * (x + y z) / (z' + w z). Exact for perspective and orthographic projections.
 */
export function depthLinearization(proj: Mat4Like, out = new Float32Array(4)): Float32Array {
    const p10 = proj[10], p11 = proj[11], p14 = proj[14], p15 = proj[15];
    // z_ndc = 2z - 1; depth = (z_ndc p15 - p14) / (z_ndc p11 - p10)
    out[0] = -p15 - p14;
    out[1] = 2 * p15;
    out[2] = -p11 - p10;
    out[3] = 2 * p11;
    return out;
}

export interface FragmentRect {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

/**
 * Fragment rectangle covering the boxes on screen (clamped to the target, grown a pixel), the whole
 * target when a box reaches behind the camera, null when none is on screen.
 */
export function boxesFragmentRect(boxes: Iterable<{ min: ArrayLike<number>; max: ArrayLike<number> }>, worldToFrag: Mat4Like, space: FragmentSpace): FragmentRect | null {
    const m = worldToFrag;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const box of boxes) {
        for (let i = 0; i < 8; i++) {
            const x = i & 1 ? box.max[0] : box.min[0];
            const y = i & 2 ? box.max[1] : box.min[1];
            const z = i & 4 ? box.max[2] : box.min[2];
            const w = m[3] * x + m[7] * y + m[11] * z + m[15];
            if (!(w > 1e-6)) return { x0: 0, y0: 0, x1: space.width, y1: space.height };
            const fx = (m[0] * x + m[4] * y + m[8] * z + m[12]) / w;
            const fy = (m[1] * x + m[5] * y + m[9] * z + m[13]) / w;
            x0 = Math.min(x0, fx);
            y0 = Math.min(y0, fy);
            x1 = Math.max(x1, fx);
            y1 = Math.max(y1, fy);
        }
    }
    if (!Number.isFinite(x0)) return null;
    x0 = Math.max(0, Math.floor(x0) - 1);
    y0 = Math.max(0, Math.floor(y0) - 1);
    x1 = Math.min(space.width, Math.ceil(x1) + 1);
    y1 = Math.min(space.height, Math.ceil(y1) + 1);
    return x1 > x0 && y1 > y0 ? { x0, y0, x1, y1 } : null;
}

/**
 * The scissor (PlayCanvas device coordinates) covering a fragment rectangle. WebGL scissors in
 * fragment coordinates; WebGPU's setScissor flips y itself unless the target is flipped.
 */
export function scissorForRect(rect: FragmentRect, targetHeight: number, webgpu: boolean, flipY: boolean): { x: number; y: number; w: number; h: number } {
    const w = rect.x1 - rect.x0;
    const h = rect.y1 - rect.y0;
    const y = webgpu && !flipY ? targetHeight - rect.y1 : rect.y0;
    return { x: rect.x0, y, w, h };
}

// ── runtime ──────────────────────────────────────────────────────────────────────────────

/** A water plane on screen, as the split sees it (waterSystem.ts splitPlanes). */
export interface WaterSplitPlane extends PlaneLike {
    /** World height above the surface beyond which a splat counts as in front (the water's coverage fade start). */
    splitHeight: number;
    /** World bounds of the water mesh. */
    aabb: pc.BoundingBox;
}

export interface WaterSplatSplitDeps {
    app: pc.AppBase;
    /** Main camera entity. */
    camera: pc.Entity;
    worldLayer: pc.Layer;
    waterLayer: pc.Layer;
    /** High / low tier water planes on screen this frame. */
    planes: () => ReadonlyArray<WaterSplitPlane>;
    /** The scene has water: keep the hook installed. */
    wanted: () => boolean;
    /** Split this frame (default: whenever water is on screen). False holds it off for the frame. */
    neededNow?: () => boolean;
    /** Milliseconds after the scene stops wanting the hook before it is removed (default 3000). */
    releaseAfterMs?: number;
    now?: () => number;
    /**
     * Install the shared splat vertex clip (effects/gsplat-mirror-clip.ts, as mirrors and water
     * reflections do), so the CPU-sort renderer can cull the water's depth map per draw.
     */
    ensureVertexClip?: () => void;
    /** Phones and performance mode: the CPU-sort splat budget is halved. */
    lowPower?: () => boolean;
    /** CPU-sort renderer: split only while the main splat draw holds at most this many splats. */
    cpuSortSplatLimit?: number;
    /** CPU-sort renderer: split only while the water's screen rectangle covers at least this share of the frame. */
    cpuSortMinScreenFraction?: number;
}

/**
 * The CPU-sort renderer (WebGL2) re-runs its whole vertex stage for every splat in pass B and in
 * the water's depth map (the GPU-sort renderer reads its projection cache instead): measured on the
 * golf scan (4.45M splats, RTX 4080) the split cost +45% GPU time there against +11% on WebGPU. So on
 * CPU sort the split runs only within a splat budget and for water large on screen; beyond it the
 * water draws as before.
 */
export const CPU_SORT_SPLIT_SPLAT_LIMIT = 1_500_000;
export const CPU_SORT_SPLIT_MIN_SCREEN_FRACTION = 0.03;

interface CulledInstances {
    opaque: pc.MeshInstance[];
    transparent: pc.MeshInstance[];
}
type LayerWithCulling = pc.Layer & { getCulledInstances(camera: pc.Camera): CulledInstances | undefined };
interface RendererInternals {
    renderForwardLayer(
        camera: pc.Camera,
        renderTarget: pc.RenderTarget | null,
        layer: pc.Layer | null,
        transparent: boolean | undefined,
        shaderPass: number,
        options: { meshInstances: pc.MeshInstance[]; drawCallback?: (drawCall: pc.MeshInstance, index: number) => void }
    ): void;
    setupViewport(camera: pc.Camera, renderTarget: pc.RenderTarget | null): void;
}
interface GSplatParamsInternals {
    material?: pc.Material;
    fisheye?: number;
    stochastic?: boolean;
    alphaClip?: number;
    currentRenderer?: number;
}
interface SplatWorldInternals {
    currentVersion?: number;
    getState?(version: number): { totalActiveSplats?: number } | undefined;
}
interface GSplatDirectorInternals {
    camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: { world?: SplatWorldInternals } | null }> }>;
}

const MODE_OFF = new Float32Array([0, 0, 0, 0]);
const GPU_SORT_RENDERER = (pc as unknown as { GSPLAT_RENDERER_RASTER_GPU_SORT?: number }).GSPLAT_RENDERER_RASTER_GPU_SORT ?? 2;

/** The engine draws the unified splats with the GPU-sort renderer (projection cache) rather than the CPU-sort quads. */
export function usesGpuSortRenderer(gsplat: { currentRenderer?: number } | null | undefined): boolean {
    return gsplat?.currentRenderer === GPU_SORT_RENDERER;
}

/** Share of a target a fragment rectangle covers (0..1). */
export function rectScreenFraction(rect: FragmentRect | null, width: number, height: number): number {
    if (!rect || width <= 0 || height <= 0) return 0;
    return ((rect.x1 - rect.x0) * (rect.y1 - rect.y0)) / (width * height);
}

export class WaterSplatSplit {
    private readonly deps: WaterSplatSplitDeps;
    private readonly device: pc.GraphicsDevice;
    private readonly lang: 'glsl' | 'wgsl';
    private readonly source: string;
    private installed = false;
    private idleSince = -1;
    /** This frame's split, decided in prerender (before the frame graph is built). */
    private frame: { choice: WaterSplitChoice; boxes: Array<{ min: number[]; max: number[] }> } | null = null;
    /** Pass A ran with the split this frame: pass B must follow after the water. */
    private passAActive = false;
    private rect: FragmentRect | null = null;
    /** This frame's splats go through the GPU-sort renderer (WebGPU's projection cache). */
    private gpuSort = true;
    /** Why the split is off this frame although everything else asked for it (diagnostics). */
    private skipped: string | null = null;
    private readonly cullPlane = new Float32Array(4);
    private readonly draws = new Set<pc.MeshInstance>();
    private readonly list: pc.MeshInstance[] = [];
    // scratch
    private readonly viewProj = new Float64Array(16);
    private readonly toFrag = new Float64Array(16);
    private readonly viewMat = new pc.Mat4();
    private readonly modeValue = new Float32Array(4);
    private readonly planeValue = new Float32Array(4);
    private readonly rectValue = new Float32Array(4);
    private readonly depthValue = new Float32Array(4);
    private readonly onPrerender: () => void;
    private readonly onPreRenderLayer: (camera: pc.CameraComponent, layer: pc.Layer, transparent: boolean) => void;
    private readonly onPostRenderLayer: (camera: pc.CameraComponent, layer: pc.Layer, transparent: boolean) => void;
    /** Frames the split was on (diagnostics). */
    activeFrames = 0;

    constructor(deps: WaterSplatSplitDeps) {
        this.deps = deps;
        this.device = deps.app.graphicsDevice;
        this.lang = this.device.isWebGPU ? 'wgsl' : 'glsl';
        this.source = this.lang === 'wgsl' ? WATER_SPLIT_PS_WGSL : WATER_SPLIT_PS_GLSL;
        this.bindOff();
        this.onPrerender = () => this.prerender();
        this.onPreRenderLayer = (camera, layer, transparent) => this.preRenderLayer(camera, layer, transparent);
        this.onPostRenderLayer = (camera, layer, transparent) => this.postRenderLayer(camera, layer, transparent);
        deps.app.on('prerender', this.onPrerender);
        deps.app.scene.on('prerender:layer', this.onPreRenderLayer);
        deps.app.scene.on('postrender:layer', this.onPostRenderLayer);
    }

    /** True while this frame's World splat draw is split at the water (decided before the frame graph is built). */
    active(): boolean {
        return !!this.frame;
    }

    /** The hook is installed on the template material (diagnostics). */
    get isInstalled(): boolean {
        return this.installed;
    }

    /** Diagnostics: the frame's split. */
    debugInfo(): Record<string, unknown> {
        return {
            installed: this.installed,
            active: !!this.frame,
            activeFrames: this.activeFrames,
            renderer: this.gpuSort ? 'gpu-sort' : 'cpu-sort',
            skipped: this.skipped,
            plane: this.frame ? { ...this.frame.choice } : null,
            rect: this.rect ? { ...this.rect } : null,
        };
    }

    /**
     * CPU-sort renderer: the vertex-stage plane (`uMirrorClipPlane`, read by mirrorClipSplat in every
     * composed splat vertex chunk) that keeps only splats behind the water or within its split
     * height, so SplatDepthPass can draw the water's map with the cheaper prepass shader. Null on
     * the GPU-sort renderer (its projection runs once per frame, not per draw) or when the template
     * material's vertex chunk doesn't call mirrorClipSplat: the fragment hook's depth mode then.
     */
    vertexCullPlane(): Float32Array | null {
        if (!this.frame || this.gpuSort) return null;
        const vs = this.templateMaterial()?.getShaderChunks(this.lang).get('gsplatModifyVS');
        if (!vs || !vs.includes('mirrorClipSplat(')) return null;
        const c = this.frame.choice;
        // mirrorClipSplat keeps a splat where dot(centre, n) - w >= 0, i.e. here d + height - n.centre >= 0
        this.cullPlane[0] = -c.nx;
        this.cullPlane[1] = -c.ny;
        this.cullPlane[2] = -c.nz;
        this.cullPlane[3] = -(c.d + c.height);
        return this.cullPlane;
    }

    /**
     * Uniforms for drawing the water's depth map into `target` (SplatDepthPass): linear depth of
     * the fragments behind the water, above `alphaClip`. Returns false when the split is off.
     */
    bindDepthMode(target: pc.RenderTarget, alphaClip: number): boolean {
        if (!this.frame) return false;
        if (!this.bindForTarget(target, WATER_SPLIT_MODE.depth, alphaClip)) {
            this.bindOff();
            return false;
        }
        return true;
    }

    /** Every draw outside the split's own windows sees the hook off. */
    bindOff(): void {
        this.device.scope.resolve(WATER_SPLIT_UNIFORMS.mode).setValue(MODE_OFF);
        this.device.scope.resolve(WATER_SPLIT_UNIFORMS.plane).setValue(MODE_OFF);
        this.device.scope.resolve(WATER_SPLIT_UNIFORMS.rect).setValue(MODE_OFF);
        this.device.scope.resolve(WATER_SPLIT_UNIFORMS.depth).setValue(MODE_OFF);
    }

    destroy(): void {
        const { app } = this.deps;
        app.off('prerender', this.onPrerender);
        app.scene.off('prerender:layer', this.onPreRenderLayer);
        app.scene.off('postrender:layer', this.onPostRenderLayer);
        this.uninstall();
        this.frame = null;
        this.bindOff();
    }

    // ── per frame ────────────────────────────────────────────────────────────────────────

    private templateMaterial(): pc.Material | null {
        return ((this.deps.app.scene as unknown as { gsplat?: GSplatParamsInternals }).gsplat?.material) ?? null;
    }

    private install(): boolean {
        const material = this.templateMaterial();
        if (!material) return false;
        const chunks = material.getShaderChunks(this.lang);
        const current = chunks.get('gsplatModifyPS');
        if (current === this.source) {
            this.installed = true;
            return true;
        }
        // someone else's fragment hook: leave it alone (the split stays off)
        if (current !== undefined) {
            this.installed = false;
            return false;
        }
        chunks.set('gsplatModifyPS', this.source);
        material.update();
        this.installed = true;
        this.deps.app.renderNextFrame = true;
        return true;
    }

    private uninstall(): void {
        this.installed = false;
        const material = this.templateMaterial();
        if (!material) return;
        const chunks = material.getShaderChunks(this.lang);
        if (chunks.get('gsplatModifyPS') === this.source) {
            chunks.delete('gsplatModifyPS');
            material.update();
        }
    }

    private prerender(): void {
        this.frame = null;
        this.passAActive = false;
        this.skipped = null;
        const now = this.deps.now?.() ?? performance.now();
        const wanted = this.deps.wanted();
        if (wanted) {
            this.idleSince = -1;
            if (!this.install()) return;
        } else {
            if (this.installed) {
                if (this.idleSince < 0) this.idleSince = now;
                if (now - this.idleSince >= (this.deps.releaseAfterMs ?? 3000)) this.uninstall();
            }
            return;
        }
        if (this.deps.neededNow && !this.deps.neededNow()) return;
        const cam = this.deps.camera.camera;
        if (!cam || !cam.enabled || !this.deps.camera.enabled) return;
        const camera = cam.camera as pc.Camera & { xrActive?: boolean };
        if (camera.xrActive) return;
        // a sub-rect viewport or a scissored camera: fragments no longer map to the whole target
        const rect = cam.rect;
        const sc = cam.scissorRect;
        if (rect.x !== 0 || rect.y !== 0 || rect.z !== 1 || rect.w !== 1 || sc.x !== 0 || sc.y !== 0 || sc.z !== 1 || sc.w !== 1) return;
        const gsplat = (this.deps.app.scene as unknown as { gsplat?: GSplatParamsInternals }).gsplat;
        // fisheye splats aren't projected with the camera matrix; stochastic splats write depth (no order issue)
        if ((gsplat?.fisheye ?? 0) > 0 || gsplat?.stochastic) return;
        // the water's depth map holds float depth (the split's depth mode writes it directly)
        if (!this.device.textureFloatRenderable) return;
        const { waterLayer } = this.deps;
        if (!waterLayer.enabled || !cam.layers.includes(waterLayer.id) || !cam.layers.includes(this.deps.worldLayer.id)) return;
        const planes = this.deps.planes();
        if (!planes.length) return;
        const choice = chooseWaterSplitPlane(planes, this.deps.camera.getPosition());
        if (!choice) return;
        const boxes = planes.map((p) => ({ min: toArray(p.aabb.getMin()), max: toArray(p.aabb.getMax()) }));
        this.gpuSort = usesGpuSortRenderer(gsplat);
        if (!this.gpuSort) {
            // CPU sort: every extra splat pass re-runs the vertex stage for all splats (see CPU_SORT_SPLIT_SPLAT_LIMIT)
            const limit = (this.deps.cpuSortSplatLimit ?? CPU_SORT_SPLIT_SPLAT_LIMIT) * (this.deps.lowPower?.() ? 0.5 : 1);
            const count = this.mainSplatCount();
            if (count > limit) {
                this.skipped = `cpu-sort: ${count} splats over ${limit}`;
                return;
            }
            const share = rectScreenFraction(this.fragmentRect(null, boxes), this.device.width, this.device.height);
            if (share < (this.deps.cpuSortMinScreenFraction ?? CPU_SORT_SPLIT_MIN_SCREEN_FRACTION)) {
                this.skipped = `cpu-sort: water covers ${share.toFixed(3)} of the frame`;
                return;
            }
            // the water's depth map culls in the vertex stage (vertexCullPlane): installed before
            // this frame's materials are synced, so the first split frame already has it
            this.deps.ensureVertexClip?.();
        }
        this.frame = { choice, boxes };
        this.activeFrames++;
    }

    /** Splats in the main camera's World draw now (the unified world state the renderer draws). */
    private mainSplatCount(): number {
        const director = (this.deps.app as unknown as { renderer?: { gsplatDirector?: GSplatDirectorInternals | null } }).renderer?.gsplatDirector;
        const world = director?.camerasMap?.get(this.deps.camera.camera?.camera)?.layersMap?.get(this.deps.worldLayer)?.gsplatManager?.world;
        return world?.getState?.(world.currentVersion ?? 0)?.totalActiveSplats ?? 0;
    }

    /** The water's fragment rectangle in a target (the backbuffer for null) with the camera now. */
    private fragmentRect(target: pc.RenderTarget | null, boxes: Array<{ min: number[]; max: number[] }>): FragmentRect | null {
        const camComp = this.deps.camera.camera;
        if (!camComp) return null;
        const width = target ? target.width : this.device.width;
        const height = target ? target.height : this.device.height;
        const space: FragmentSpace = { width, height, yDown: fragmentYDown(this.device.isWebGPU, !!target?.flipY) };
        // the view matrix as the renderer builds it (position and rotation, unit scale)
        const node = this.deps.camera;
        this.viewMat.setTRS(node.getPosition(), node.getRotation(), pc.Vec3.ONE).invert();
        mul4(camComp.camera.projectionMatrix.data, this.viewMat.data, this.viewProj);
        worldToFragment(this.viewProj, space, this.toFrag);
        return boxesFragmentRect(boxes, this.toFrag, space);
    }

    /** Compute and bind the split uniforms for a target; false when the water is off screen there. */
    private bindForTarget(target: pc.RenderTarget | null, mode: number, alphaClip = 0): boolean {
        const frame = this.frame;
        const camComp = this.deps.camera.camera;
        if (!frame || !camComp) return false;
        const camera = camComp.camera;
        // fills this.toFrag for the target
        const rect = this.fragmentRect(target, frame.boxes);
        if (!rect) return false;
        if (!splitPlaneCoefficients(this.toFrag, frame.choice, this.planeValue)) return false;
        depthLinearization(camera.projectionMatrix.data, this.depthValue);
        this.rect = rect;
        this.rectValue[0] = rect.x0;
        this.rectValue[1] = rect.y0;
        this.rectValue[2] = rect.x1;
        this.rectValue[3] = rect.y1;
        this.modeValue[0] = mode;
        this.modeValue[1] = alphaClip;
        const scope = this.device.scope;
        scope.resolve(WATER_SPLIT_UNIFORMS.mode).setValue(this.modeValue);
        scope.resolve(WATER_SPLIT_UNIFORMS.plane).setValue(this.planeValue);
        scope.resolve(WATER_SPLIT_UNIFORMS.rect).setValue(this.rectValue);
        scope.resolve(WATER_SPLIT_UNIFORMS.depth).setValue(this.depthValue);
        return true;
    }

    private activeTarget(): pc.RenderTarget | null {
        const device = this.device as pc.GraphicsDevice & { renderTarget?: pc.RenderTarget | null; backBuffer?: pc.RenderTarget | null };
        return device.renderTarget ?? device.backBuffer ?? null;
    }

    private preRenderLayer(camera: pc.CameraComponent, layer: pc.Layer, transparent: boolean): void {
        if (!this.frame || !transparent || layer !== this.deps.worldLayer || camera !== this.deps.camera.camera) return;
        // Views attached for this render only (the stereo renderer sets them on 'prerender', maybe
        // after this frame's decision; an XR session): one projection no longer covers the target.
        if ((camera.camera as pc.Camera & { xrActive?: boolean }).xrActive) {
            this.passAActive = false;
            this.bindOff();
            return;
        }
        this.passAActive = this.bindForTarget(this.activeTarget(), WATER_SPLIT_MODE.behind);
        if (!this.passAActive) this.bindOff();
    }

    private postRenderLayer(camera: pc.CameraComponent, layer: pc.Layer, transparent: boolean): void {
        if (!this.passAActive || !transparent || camera !== this.deps.camera.camera) return;
        if (layer === this.deps.worldLayer) {
            this.bindOff();
        } else if (layer === this.deps.waterLayer) {
            this.passAActive = false;
            try {
                this.drawFront(camera);
            } finally {
                this.bindOff();
            }
        }
    }

    /** Pass B: the main camera's unified splat draw again, only the fragments in front of the water. */
    private drawFront(cameraComponent: pc.CameraComponent): void {
        const camera = cameraComponent.camera;
        const culled = (this.deps.worldLayer as LayerWithCulling).getCulledInstances(camera);
        const list = this.list;
        list.length = 0;
        if (culled) {
            // only the unified draws: they copy the template material and its hook, and pass A
            // dropped their front fragments (a non-unified splat drew whole and needs nothing)
            const draws = collectUnifiedSplatDraws(this.deps.app, this.draws);
            for (const mi of culled.transparent) if (draws.has(mi)) list.push(mi);
            for (const mi of culled.opaque) if (draws.has(mi)) list.push(mi);
        }
        if (!list.length) return;
        const target = this.activeTarget();
        if (!this.bindForTarget(target, WATER_SPLIT_MODE.front) || !this.rect) {
            list.length = 0;
            return;
        }
        const renderer = (this.deps.app as unknown as { renderer: RendererInternals }).renderer;
        const height = target ? target.height : this.device.height;
        const scissor = scissorForRect(this.rect, height, this.device.isWebGPU, !!target?.flipY);
        const device = this.device as pc.GraphicsDevice & { setScissor(x: number, y: number, w: number, h: number): void };
        const shaderPass = (camera as unknown as { shaderPassInfo?: { index: number } }).shaderPassInfo?.index ?? pc.SHADER_FORWARD;
        try {
            renderer.renderForwardLayer(camera, target, null, undefined, shaderPass, {
                meshInstances: list,
                // after the renderer's own viewport / scissor setup, right before the draw
                drawCallback: () => device.setScissor(scissor.x, scissor.y, scissor.w, scissor.h),
            });
        } finally {
            list.length = 0;
            renderer.setupViewport(camera, target);
            (device as unknown as { setDrawStates?: () => void }).setDrawStates?.();
        }
    }
}

function toArray(v: pc.Vec3): number[] {
    return [v.x, v.y, v.z];
}
